import React, { useContext } from 'react';
import styled from 'styled-components';
import HamQuest from '../../api/HamQuestGiver';
import { QuestRoute } from '../../api/QuestRoutes';
import { useParams } from 'react-router-dom';
import { useGlobalState } from '../../stateProvider/StateProvider';
import { getDaysInMonth, getMonths, translate } from '../../utility/functions';
import Button from '../../forms/Button';
import Input from '../../forms/Input';
import FormSetting from '../../layout/FormSetting';
import Select from '../../forms/Select';
import { HSOptions } from '../../../constants/HSOptions';
import Checkbox from '../../forms/Checkbox';
import PageBreak from '../../layout/PageBreak';
import Row from '../../layout/Row';
import LoyaltyPoints from './LoyaltyPoints';
import { userRoles } from '../../RoleProtected';
import PhoneNumberInput from '../../forms/formComponents/PhoneNumberInput';
import { UserContexts } from '../../stateProvider/UsersContext';
import ModalFactory from '../../Modal';
import UserMedia from '../../processors/UserMedias';
import Col from '../../layout/Col';

const Management = ({ userData, ...props }) => {
    const {
        state: {
            states,
            locations
        },
    } = useGlobalState();

    const context = useContext(UserContexts);

    const { guid } = useParams();

    const months = getMonths();

    const lockouEndDate = new Date(context.user.lockoutEnd);
    const loginIsLocked = lockouEndDate > new Date();

    const loyaltyMedias = context.user.loyaltyMedias?.filter(e => e.mediaType !== 'AppId');

    const resendConfirmation = () => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Resend Confirmation Email?`,
                body: (
                    <Col>
                        <span>Are you sure you want to re-send a confirmation Email?</span>
                        <span>Sending to: <b>{context.user.email}</b></span>
                    </Col>
                ),
                type: 'warning',
            },
            promise: {
                title: 'Sending',
                body: `Sending, please wait.`,
                callback: () => HamQuest.get(QuestRoute.user.resendConfirmation(guid))
            },
            confirmed: {
                title: 'Success',
                body: `Confirmation email has been sent.`,
                type: 'success'
            },
            finally: () => context.refreshUser()
        })
    }

    return (
        <PageBreak>
            <FormSetting
                label='Profile'
                inputs={{ ...context.user }}
                onSubmit={e => HamQuest.put(QuestRoute.user.info(guid), { ...e })
                    .then(() => context.refreshUser())
                }
                controls={[context.user.userStatus === 'NotValidated' ? [<Button onClick={resendConfirmation} label='Resend Confirmation Email' />] : null,
                    context.user.accessFailedCount > 0 ? <Button
                        label='Clear Lockout'
                        onClick={() => HamQuest.put(QuestRoute.user.clearLockout(guid))
                            .then(() => context.refreshUser())}
                        roles={userRoles.editCustomers} 
                   /> : null
                ]}
                text='Update'
                roles={userRoles.editCustomers}
                editable
            >
                {({ values }) => (
                    <>
                        <Select
                            label='Account Status'
                            name='userStatus'
                            options={HSOptions.user.accountStatus}
                            disable={values.userStatus === 'NotValidated'}
                            required
                        />
                        <Row wrap>
                            <Input
                                label='Email'
                                name='email'
                                required
                            />
                            <PhoneNumberInput
                                name='cellPhoneNumber'
                            />
                        </Row>
                        <Row wrap>
                            <Input
                                label='First Name'
                                name='firstName'
                                required
                            />
                            <Input
                                label='Last Name'
                                name='lastName'
                                required
                            />
                        </Row>
                        <Row wrap>
                            <Input
                                label='Birth Day'
                                name='birthdayDay'
                                type='number'
                                min={1}
                                max={getDaysInMonth(values.birthdayMonth)}
                                options={months}
                            />
                            <Select
                                label='Birth Month'
                                name='birthdayMonth'
                                options={months}
                                nullable
                            />
                        </Row>
                        <Row align='flex-end' wrap>
                            <Select
                                label='Billing Location'
                                name='assignedLocation'
                                options={locations}
                                nullable
                            />
                            <Checkbox
                                label='Locked Location'
                                name='merchantLocked'
                            />
                        </Row>
                        <Row wrap>
                            <Input 
                                label='Login Locked'
                                readOnly
                                value={loginIsLocked ? 'Yes' : 'No'}
                            />
                            <Input
                                label='Failed Log In Attempts'
                                name='accessFailedCount'
                                type='number'
                                readOnly
                            />
                            <Input
                                label='Lockout End'
                                name='lockoutEnd'
                                readOnly
                                value={loginIsLocked ? lockouEndDate.toLocaleString() : ''}   
                                />
                        </Row>
                        <Row>
                            <Col>
                                <Input
                                    label='Marketing Opt-In:'
                                    name='marketingOptIn_ReadOnly'
                                    value={values.optInToMarketing ? 'Yes' : 'No'}
                                    required
                                    disabled
                                />                            
                            </Col>
                        </Row>
                    </>
                )}
            </FormSetting>


            {<FormSetting
                label='User Media'
                controls={[
                    <Button
                        onClick={() => ModalFactory.show({ title: 'User Media', body: <UserMedia userId={context.user.userId} states={states} refreshState={context.refreshUser} /> })}
                        label='Edit User Media'
                        icon='edit'
                    />]}
                wrapper
            >
                {loyaltyMedias.length ? loyaltyMedias.map((e, i) => (
                    <Input
                        key={`LM-${i}`}
                        label={translate(e.mediaType) + (e.backupPlate ? ' - (Backup)' : '')}
                        value={e.mediaId}
                        standalone
                        readOnly
                    />
                )) : <span>No Media on file.</span>}
            </FormSetting>}

            <FormSetting
                label='Vehicle'
                getInputs={QuestRoute.user.vehicle(guid)}
                onSubmit={e => HamQuest.put(QuestRoute.user.vehicle(guid), { ...e })}
                text='Update'
                editable
                roles={userRoles.editCustomers}
            >
                <Select
                    label='State'
                    name='state'
                    options={states}
                    nullable
                />
                <Input
                    label='Make'
                    name='make'
                    validate={/^(?!.*not\s+a).*$/gi}
                />
                <Input
                    label='Model'
                    name='model'
                    validate={/^(?!.*not\s+a).*$/gi}
                />
                <Input
                    label='Year'
                    name='year'
                    type='number'
                />
                <Input
                    label='Color'
                    name='color'
                    validate={/^(?!.*not\s+a).*$/gi}
                />
            </FormSetting>

            <LoyaltyPoints />
        </PageBreak >
    );
}

export default Management;
